// What checkout says about the food on a bill (boards TicketNote, TicketUnsent). It never stops
// the sale: paying before the food is ready is normal.
import type { OrderDetail } from '@ims/data'
import { Button } from '@ims/ui'
import { AlertTriangle, ChefHat, Clock, Printer } from '@ims/ui/icons'
import { useOnline } from '../hooks.ts'
import { dishNames, kitchenCheck, unsentCount } from '../lib/orders.ts'
import { useStore } from '../workspace.tsx'
import { sendOrderToKitchen } from './order-actions.tsx'
import { useOrderActionDeps } from './service.tsx'

const dishes = (n: number) => (n === 1 ? '1 dish' : `${n} dishes`)

export function KitchenCheckoutNote({
  detail,
  where,
}: {
  detail: OrderDetail
  where: string | null
}) {
  const actions = useOrderActionDeps()
  const online = useOnline()
  const { engine } = useStore()
  const { unsent, notReady } = kitchenCheck(detail)
  if (unsent.length > 0) {
    const send = async () => {
      // With no internet the kitchen's screen can't hear: the paper is how it finds out.
      await sendOrderToKitchen(actions, detail, where, { forcePrint: !online })
      void engine.syncNow().catch(() => {})
    }
    return (
      <div role="alert" className="rounded-xl bg-warning-light p-3">
        <div className="flex gap-2.5">
          <AlertTriangle className="mt-0.5 size-[18px] shrink-0 text-warning" aria-hidden />
          <div>
            <p className="font-bold text-warning">
              {dishes(unsent.length)} not sent to the kitchen
            </p>
            <p className="text-sm">{dishNames(unsent)}. They can’t be sent after payment.</p>
          </div>
        </div>
        <Button
          variant="secondary"
          className="mt-2.5 w-full"
          loading={actions.busy}
          icon={online ? <ChefHat className="size-4" /> : <Printer className="size-4" />}
          onClick={() => void send()}
        >
          {online ? 'Send to kitchen' : 'Send and print ticket'} ({unsentCount(detail)})
        </Button>
      </div>
    )
  }
  if (notReady.length === 0) return null
  return (
    <div role="status" className="flex gap-2.5 rounded-xl bg-soft p-3">
      <Clock className="mt-0.5 size-[18px] shrink-0 text-muted" aria-hidden />
      <div>
        <p className="font-semibold">{dishes(notReady.length)} not marked ready</p>
        <p className="text-sm text-muted">
          {dishNames(notReady)}. They stay on the kitchen screen after payment.
        </p>
      </div>
    </div>
  )
}
